// ── Design tokens (mirrored for non-React use) ────────────────────────────────
const GREEN  = '#27ae60';
const AMBER  = '#f59e0b';
const RED    = '#E96A6A';

const NINETY_DAYS_MS   = 90  * 86400000;
const FOURTEEN_DAYS_MS = 14  * 86400000;

// ── Score labelling ───────────────────────────────────────────────────────────
export function getScoreLabel(score) {
  if (score >= 80) return 'Healthy';
  if (score >= 50) return 'Needs attention';
  return 'At risk';
}

export function getScoreColor(score) {
  if (score >= 80) return GREEN;
  if (score >= 50) return AMBER;
  return RED;
}

export function getScoreBg(score) {
  if (score >= 80) return '#f0faf4';
  if (score >= 50) return '#fffbeb';
  return 'rgba(233,106,106,0.1)';
}

// ── Factor 1 — Last contact (40 pts) ─────────────────────────────────────────
function scoreLastContact(contact) {
  const interactions = contact.interactions || [];
  if (!interactions.length) {
    return {
      points: 0, max: 40, label: 'Last contact',
      insight: 'No interactions recorded — reach out to start building the relationship',
    };
  }

  const lastMs  = Math.max(...interactions.map(i => new Date(i.date).getTime()));
  const days    = Math.floor((Date.now() - lastMs) / 86400000);

  let points;
  if      (days <=  7) points = 40;
  else if (days <= 14) points = 30;
  else if (days <= 30) points = 15;
  else if (days <= 60) points =  5;
  else                 points =  0;

  let insight;
  if      (days <=  7) insight = `Last contacted ${days} day${days !== 1 ? 's' : ''} ago — relationship is active`;
  else if (days <= 14) insight = `Last contacted ${days} days ago — follow up soon`;
  else if (days <= 30) insight = `Last contacted ${days} days ago — consider reaching out`;
  else if (days <= 60) insight = `Last contacted ${days} days ago — this relationship needs attention`;
  else                 insight = `No contact in ${days} days — relationship at risk`;

  return { points, max: 40, label: 'Last contact', insight };
}

// ── Factor 2 — Email engagement (30 pts) ─────────────────────────────────────
// The interaction model does not record message direction (sent vs received),
// so email interaction frequency over 90 days is used as an engagement proxy.
function scoreResponseRate(contact) {
  const cutoff      = Date.now() - NINETY_DAYS_MS;
  const recentEmails = (contact.interactions || []).filter(
    i => i.type === 'email' && new Date(i.date).getTime() >= cutoff
  );
  const count = recentEmails.length;

  let points, insight;
  if (count === 0) {
    points = 0;
    insight = 'No email interactions in the last 90 days';
  } else if (count < 3) {
    points = 10;
    insight = `${count} email interaction${count > 1 ? 's' : ''} in the last 90 days — low engagement`;
  } else if (count < 5) {
    points = 20;
    insight = `${count} email interactions in the last 90 days — moderate engagement`;
  } else {
    points = 30;
    insight = `${count} email interactions in the last 90 days — strong engagement`;
  }

  return { points, max: 30, label: 'Email engagement', insight };
}

// ── Factor 3 — Active work (20 pts) ──────────────────────────────────────────
function scoreActiveWork(contact, tasks) {
  const now          = Date.now();
  const futureCutoff = now + FOURTEEN_DAYS_MS;
  const nameLower    = (contact.name    || '').toLowerCase();
  const companyLower = (contact.company || '').toLowerCase();

  function isLinked(task) {
    if (!nameLower) return false;
    const title = (task.title || '').toLowerCase();
    const notes = (task.notes || task.description || '').toLowerCase();
    return title.includes(nameLower) || notes.includes(nameLower)
      || (companyLower && (title.includes(companyLower) || notes.includes(companyLower)));
  }

  const openTasks        = tasks.filter(t => !t.completed && t.taskType !== 'meeting' && isLinked(t));
  const upcomingMeetings = tasks.filter(t => {
    if (t.completed || t.taskType !== 'meeting') return false;
    if (!isLinked(t)) return false;
    const dt = new Date(t.datetime || t.date).getTime();
    return dt > now && dt <= futureCutoff;
  });

  const hasOpenTasks = openTasks.length > 0;
  const hasMeeting   = upcomingMeetings.length > 0;
  const points       = (hasOpenTasks ? 10 : 0) + (hasMeeting ? 10 : 0);

  let insight;
  if (hasOpenTasks && hasMeeting) {
    insight = `${openTasks.length} open task${openTasks.length > 1 ? 's' : ''} and a meeting in the next 14 days`;
  } else if (hasOpenTasks) {
    insight = `${openTasks.length} open task${openTasks.length > 1 ? 's' : ''} linked to this client — no upcoming meetings`;
  } else if (hasMeeting) {
    insight = 'Meeting scheduled in the next 14 days — no linked open tasks';
  } else {
    insight = 'No open tasks or upcoming meetings linked to this client';
  }

  return { points, max: 20, label: 'Active work', insight };
}

// ── Factor 4 — Invoice health (10 pts) ───────────────────────────────────────
function scoreInvoiceHealth(contact, invoices) {
  const nameLower    = (contact.name    || '').toLowerCase();
  const companyLower = (contact.company || '').toLowerCase();

  const clientInvoices = invoices.filter(inv => {
    if (inv.paid) return false;
    const field = ((inv.clientName || inv.client || '')).toLowerCase();
    return field === nameLower || (companyLower && field === companyLower);
  });

  if (!clientInvoices.length) {
    return { points: 10, max: 10, label: 'Invoice health', insight: 'No outstanding invoices' };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let maxOverdue = 0;
  for (const inv of clientInvoices) {
    if (!inv.dueDate) continue;
    const due  = new Date(inv.dueDate);
    due.setHours(0, 0, 0, 0);
    const days = Math.ceil((today - due) / 86400000);
    if (days > maxOverdue) maxOverdue = days;
  }

  let points, insight;
  if (maxOverdue <= 0) {
    points  = 10;
    insight = `${clientInvoices.length} open invoice${clientInvoices.length > 1 ? 's' : ''} — not yet overdue`;
  } else if (maxOverdue <= 14) {
    points  = 5;
    insight = `Invoice is ${maxOverdue} day${maxOverdue !== 1 ? 's' : ''} overdue`;
  } else {
    points  = 0;
    insight = `Invoice is ${maxOverdue} days overdue — follow up required`;
  }

  return { points, max: 10, label: 'Invoice health', insight };
}

// ── Public API ────────────────────────────────────────────────────────────────
export function calculateHealthScore(contact, tasks = [], invoices = []) {
  const factors = [
    scoreLastContact(contact),
    scoreResponseRate(contact),
    scoreActiveWork(contact, tasks),
    scoreInvoiceHealth(contact, invoices),
  ];

  const score = Math.min(100, factors.reduce((sum, f) => sum + f.points, 0));

  return {
    score,
    label:         getScoreLabel(score),
    color:         getScoreColor(score),
    factors,
    calculatedAt:  new Date().toISOString(),
  };
}

export function calculateAllHealthScores(contacts, tasks = [], invoices = []) {
  const result = {};
  for (const c of contacts) {
    if ((c.tags || []).includes('Client')) {
      result[c.id] = calculateHealthScore(c, tasks, invoices);
    }
  }
  return result;
}
